/**
 * SpeechController - Manages speech synthesis for individual segments
 * Handles Web Speech API interactions and provides granular control over playback
 */
import EventManager from "./EventManager.js";

export default class SpeechController extends EventManager {
	/** @type {SpeechSynthesis} Web Speech API interface */
	#synth = window.speechSynthesis;

	/** @type {SpeechSynthesisUtterance|null} Current utterance being spoken */
	#currentUtterance = null;

	/** @type {boolean} Whether speech is currently playing */
	#isPlaying = false;

	/** @type {boolean} Whether speech is currently paused */
	#isPaused = false;

	/** @type {boolean} Flag to suppress error events for intentional cancellations */
	#suppressNextError = false;

	/** @type {boolean} Whether any speech has been spoken (for timing workarounds) */
	#hasEverSpoken = false;

	/** @type {Object} Speech synthesis settings */
	#settings = {
		rate: 1.0,
		pitch: 1.0,
		volume: 1.0,
	};

	/**
	 * Initialize SpeechController
	 */
	constructor() {
		super();
		this.#loadSettings();
	}

	/**
	 * Check if Web Speech API is supported
	 * @returns {boolean} Whether speech synthesis is supported
	 */
	static isSupported() {
		return (
			"speechSynthesis" in window && "SpeechSynthesisUtterance" in window
		);
	}

	/**
	 * Detect if the current browser is Firefox
	 * @returns {boolean} Whether the browser is Firefox
	 * @private
	 */
	#isFirefox() {
		return navigator.userAgent.toLowerCase().includes("firefox");
	}

	/**
	 * Speak a text segment
	 * @param {string} text - Text to speak
	 * @param {SpeechSynthesisVoice} voice - Voice to use
	 * @returns {Promise} Promise that resolves when speech ends
	 */
	speak(text, voice = null) {
		return new Promise((resolve, reject) => {
			this.stop(true, false);

			if (!text?.trim()) {
				resolve();
				return;
			}

			try {
				this.#currentUtterance = new SpeechSynthesisUtterance(
					text.trim(),
				);

				// Apply settings
				this.#currentUtterance.rate = this.#settings.rate;
				this.#currentUtterance.pitch = this.#settings.pitch;
				this.#currentUtterance.volume = this.#settings.volume;

				if (voice) {
					this.#currentUtterance.voice = voice;
				}

				this.#currentUtterance.onstart = () => {
					this.#isPlaying = true;
					this.#isPaused = false;
					this.#hasEverSpoken = true;
					this.triggerEvent("onStart", {
						text,
						utterance: this.#currentUtterance,
					});
				};

				this.#currentUtterance.onend = () => {
					this.#isPlaying = false;
					this.#isPaused = false;
					this.triggerEvent("onEnd", {
						text,
						utterance: this.#currentUtterance,
					});
					resolve();
				};

				this.#currentUtterance.onpause = () => {
					this.#isPaused = true;
					this.triggerEvent("onPause", {
						text,
						utterance: this.#currentUtterance,
					});
				};

				this.#currentUtterance.onresume = () => {
					this.#isPaused = false;
					this.triggerEvent("onResume", {
						text,
						utterance: this.#currentUtterance,
					});
				};

				this.#currentUtterance.onerror = (event) => {
					this.#isPlaying = false;
					this.#isPaused = false;

					const suppressibleErrors = [
						"interrupted",
						"canceled",
						"cancelled",
					];
					if (
						this.#suppressNextError &&
						suppressibleErrors.includes(event.error)
					) {
						this.#suppressNextError = false;
						resolve();
						return;
					}

					this.#suppressNextError = false;

					console.warn(
						`TitusJS: Speech synthesis error: ${event.error}`,
					);
					this.triggerEvent("onError", {
						error: event.error,
						text,
						utterance: this.#currentUtterance,
					});
					reject(new Error(`Speech synthesis error: ${event.error}`));
				};

				const utteranceToSpeak = this.#currentUtterance;
				const startSpeaking = () => {
					if (
						utteranceToSpeak &&
						utteranceToSpeak === this.#currentUtterance &&
						!this.#synth.speaking
					) {
						this.#synth.speak(utteranceToSpeak);
					}
				};

				if (!this.#hasEverSpoken) {
					setTimeout(startSpeaking, 80);
				} else {
					startSpeaking();
				}
			} catch (error) {
				this.#isPlaying = false;
				this.#isPaused = false;
				reject(error);
			}
		});
	}

	/**
	 * Pause current speech
	 */
	pause() {
		if (this.#isPlaying && !this.#isPaused && this.#synth.speaking) {
			this.#synth.pause();
		}
	}

	/**
	 * Resume paused speech
	 */
	resume() {
		if (this.#isPaused && this.#synth.paused) {
			this.#synth.resume();
		}
	}

	/**
	 * Stop current speech
	 * @param {boolean} suppressError - Whether to suppress error events
	 * @param {boolean} forceClose - Whether this is a force close operation (Firefox-specific)
	 */
	stop(suppressError = false, forceClose = false) {
		if (suppressError) {
			this.#suppressNextError = true;
		}

		// Immediate state reset
		this.#isPlaying = false;
		this.#isPaused = false;

		// Firefox-specific aggressive cancellation only for force close
		if (this.#isFirefox() && forceClose) {
			this.#forceStopFirefox();
		} else {
			// Standard cancellation for other browsers and normal operations
			if (this.#synth.speaking || this.#synth.pending) {
				this.#synth.cancel();
			}
		}

		this.#currentUtterance = null;
	}

	/**
	 * Force stop speech synthesis in Firefox
	 * @private
	 */
	#forceStopFirefox() {
		// Clear any pending utterances
		if (this.#currentUtterance) {
			this.#currentUtterance.onstart = null;
			this.#currentUtterance.onend = null;
			this.#currentUtterance.onerror = null;
			this.#currentUtterance.onpause = null;
			this.#currentUtterance.onresume = null;
		}

		// Nuclear option: Try to reset the entire speech synthesis
		try {
			// Multiple cancellation attempts with different strategies
			this.#synth.cancel();

			// Immediate retry
			setTimeout(() => {
				this.#synth.cancel();
			}, 1);

			// Nuclear option: Force reset speech synthesis
			setTimeout(() => {
				if (this.#synth.speaking || this.#synth.pending) {
					// Try to completely reset the speech synthesis
					const voices = this.#synth.getVoices();
					if (voices.length > 0) {
						const resetUtterance = new SpeechSynthesisUtterance("");
						resetUtterance.voice = voices[0];
						resetUtterance.volume = 0;
						resetUtterance.rate = 0.1;
						resetUtterance.pitch = 0.1;
						this.#synth.speak(resetUtterance);
						this.#synth.cancel();
					}
				}
			}, 100);
		} catch (error) {
			console.warn("TitusJS: Error during Firefox force stop:", error);
		}
	}

	/**
	 * Update speech settings
	 * @param {Object} newSettings - Settings to update
	 * @param {number} newSettings.rate - Speech rate (0.1 - 10)
	 * @param {number} newSettings.pitch - Speech pitch (0 - 2)
	 * @param {number} newSettings.volume - Speech volume (0 - 1)
	 */
	updateSettings(newSettings) {
		if (
			typeof newSettings.rate === "number" &&
			newSettings.rate >= 0.1 &&
			newSettings.rate <= 10
		) {
			this.#settings.rate = newSettings.rate;
		}

		if (
			typeof newSettings.pitch === "number" &&
			newSettings.pitch >= 0 &&
			newSettings.pitch <= 2
		) {
			this.#settings.pitch = newSettings.pitch;
		}

		if (
			typeof newSettings.volume === "number" &&
			newSettings.volume >= 0 &&
			newSettings.volume <= 1
		) {
			this.#settings.volume = newSettings.volume;
		}

		this.#saveSettings();
	}

	/**
	 * Get current speech settings
	 * @returns {Object} Current settings
	 */
	getSettings() {
		return { ...this.#settings };
	}

	/**
	 * Get current playback state
	 * @returns {Object} State object
	 */
	getState() {
		return {
			isPlaying: this.#isPlaying,
			isPaused: this.#isPaused,
			isSupported: SpeechController.isSupported(),
			currentText: this.#currentUtterance?.text || null,
		};
	}

	/**
	 * Load settings from localStorage
	 * @private
	 */
	#loadSettings() {
		if (!this.#isLocalStorageAvailable()) return;

		try {
			const saved = localStorage.getItem("titusjs-speech-settings");
			if (saved) {
				const settings = JSON.parse(saved);
				if (
					typeof settings === "object" &&
					settings !== null &&
					!Array.isArray(settings)
				) {
					const validatedSettings = {};
					if (
						typeof settings.rate === "number" &&
						settings.rate >= 0.1 &&
						settings.rate <= 10
					) {
						validatedSettings.rate = settings.rate;
					}
					if (
						typeof settings.pitch === "number" &&
						settings.pitch >= 0 &&
						settings.pitch <= 2
					) {
						validatedSettings.pitch = settings.pitch;
					}
					if (
						typeof settings.volume === "number" &&
						settings.volume >= 0 &&
						settings.volume <= 1
					) {
						validatedSettings.volume = settings.volume;
					}
					this.#settings = {
						...this.#settings,
						...validatedSettings,
					};
				}
			}
		} catch (error) {
			console.warn(
				"TitusJS: Could not load speech settings from localStorage:",
				error,
			);
		}
	}

	/**
	 * Save settings to localStorage
	 * @private
	 */
	#saveSettings() {
		if (!this.#isLocalStorageAvailable()) {
			console.warn(
				"TitusJS: localStorage not available, speech settings will not persist",
			);
			return;
		}

		try {
			localStorage.setItem(
				"titusjs-speech-settings",
				JSON.stringify(this.#settings),
			);
		} catch (error) {
			if (error.name === "QuotaExceededError") {
				console.warn(
					"TitusJS: localStorage quota exceeded, could not save speech settings",
				);
			} else {
				console.warn(
					"TitusJS: Could not save speech settings to localStorage:",
					error,
				);
			}
		}
	}

	/**
	 * Check if localStorage is available and working
	 * @returns {boolean} Whether localStorage is available
	 * @private
	 */
	#isLocalStorageAvailable() {
		try {
			const test = "__tjs_storage_test__";
			localStorage.setItem(test, test);
			localStorage.removeItem(test);
			return true;
		} catch {
			return false;
		}
	}
}
